/**
 * api-include-fetcher.ts
 *
 * Scans SCAD source for `include <https://...>` / `include <http://...>` directives,
 * fetches the JSON from each URL (with retry), and returns a list of synthetic
 * JsonProjectFile objects that can be fed directly into preprocessJsonIncludes().
 *
 * Design notes
 * ─────────────
 * • Fully async — must be awaited before the synchronous preprocessor runs.
 * • Session-scoped cache: each URL is fetched at most once per browser session
 *   (or per cache Map instance, whichever the caller controls). Pass the same
 *   Map across compiles so re-compiles are instantaneous.
 * • Up to MAX_RETRIES attempts per URL with exponential back-off.
 * • On permanent failure the URL's entry in the result list is omitted, so the
 *   synchronous preprocessor leaves the include line untouched and the WASM
 *   compiler emits a "file not found"-style error surface to the user.
 */

import { filenameToScadName } from './json-to-scad';
import type { JsonProjectFile } from './scad-preprocessor';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_RETRIES = 3;
/** Initial delay in ms; doubles on each retry: 300 → 600 → 1200 */
const BASE_RETRY_DELAY_MS = 300;
/** Hard cap on a single fetch attempt before it is aborted. */
const FETCH_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// URL detection
// ---------------------------------------------------------------------------

/**
 * Regex that matches the path inside `include <...>` when the path looks like
 * an absolute HTTP/HTTPS URL.
 *
 * Captures:
 *   group 1 — the full URL (e.g. "https://example.com/api/v1/scores")
 */
const URL_INCLUDE_RE = /^include\s*<(https?:\/\/[^>]+)>/i;

/**
 * Derive the synthetic "filename" used to key the fetched result.
 * We take the last non-empty path segment and normalise it.
 *
 * "https://api.example.com/v1/contest/results"  → "results"
 * "https://api.example.com/data.json"           → "data.json"
 * "https://api.example.com/"                    → "api_example_com"  (fallback)
 */
export function urlToFilename(url: string): string {
  try {
    const parsed = new URL(url);
    // Split the pathname and take the last non-empty segment.
    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
      return segments[segments.length - 1];
    }
    // Fallback: use the hostname.
    return parsed.hostname.replace(/\./g, '_');
  } catch {
    // Not a valid URL — use whatever comes after the last slash.
    const afterSlash = url.split('/').pop();
    return afterSlash || 'api_data';
  }
}

/**
 * The function name used in generated SCAD is derived from the URL the same
 * way a filename would be — via filenameToScadName on the last path segment.
 */
export function urlToScadName(url: string): string {
  return filenameToScadName(urlToFilename(url));
}

// ---------------------------------------------------------------------------
// Retry fetch
// ---------------------------------------------------------------------------

/**
 * Fetch a URL and return its text body, retrying up to MAX_RETRIES times on
 * transient failures (network errors, 5xx responses).
 *
 * Throws an error if all attempts fail.
 */
async function fetchWithRetry(url: string): Promise<string> {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      const delay = BASE_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
      await new Promise((res) => setTimeout(res, delay));
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      // Treat 4xx as permanent failures (no retry), 5xx as transient.
      if (response.status >= 400 && response.status < 500) {
        throw new Error(
          `HTTP ${response.status} ${response.statusText} — not retrying`,
        );
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      return await response.text();
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;

      // 4xx: bail immediately.
      if (
        err instanceof Error &&
        err.message.startsWith('HTTP 4') &&
        err.message.includes('not retrying')
      ) {
        throw err;
      }

      console.warn(
        `[api-include] Fetch attempt ${attempt + 1}/${MAX_RETRIES} failed for ${url}:`,
        err,
      );
    }
  }

  throw (
    lastError ??
    new Error(`Failed to fetch ${url} after ${MAX_RETRIES} attempts`)
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Result of resolving a single API include URL.
 */
export interface ApiIncludeResult {
  url: string;
  filename: string;
  scadName: string;
  /** null when the fetch failed */
  content: string | null;
  error: string | null;
}

/**
 * Scan `code` for `include <http(s)://...>` lines, fetch each unique URL
 * (using `cache` to avoid redundant fetches within a session), and return
 * synthetic `JsonProjectFile` entries for every successfully fetched URL.
 *
 * Failed URLs are returned in the `errors` array so callers can surface them.
 *
 * @param code   Raw SCAD source to scan.
 * @param cache  Session-scoped Map<url, content>.  Pass the same Map instance
 *               across multiple `resolveApiIncludes` calls to benefit from
 *               caching.
 */
export async function resolveApiIncludes(
  code: string,
  cache: Map<string, string>,
): Promise<{
  files: JsonProjectFile[];
  errors: ApiIncludeResult[];
}> {
  // Collect unique URLs in order of appearance.
  const urlsSeen = new Set<string>();
  for (const line of code.split('\n')) {
    const m = line.trim().match(URL_INCLUDE_RE);
    if (m) {
      urlsSeen.add(m[1].trim());
    }
  }

  if (urlsSeen.size === 0) {
    return { files: [], errors: [] };
  }

  const files: JsonProjectFile[] = [];
  const errors: ApiIncludeResult[] = [];

  await Promise.all(
    Array.from(urlsSeen).map(async (url) => {
      const filename = urlToFilename(url);
      const scadName = urlToScadName(url);

      // Cache hit.
      if (cache.has(url)) {
        files.push({ filename, content: cache.get(url)! });
        return;
      }

      try {
        const text = await fetchWithRetry(url);
        cache.set(url, text);
        files.push({ filename, content: text });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error(`[api-include] Failed to fetch ${url}: ${message}`);
        errors.push({ url, filename, scadName, content: null, error: message });
      }
    }),
  );

  return { files, errors };
}

/**
 * Returns all unique HTTP/HTTPS URLs referenced via `include <url>` in the
 * given SCAD source.  Used by callers that need to know which URLs are
 * referenced before actually fetching them.
 */
export function parseApiIncludes(code: string): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();
  for (const line of code.split('\n')) {
    const m = line.trim().match(URL_INCLUDE_RE);
    if (m) {
      const url = m[1].trim();
      if (!seen.has(url)) {
        seen.add(url);
        urls.push(url);
      }
    }
  }
  return urls;
}
